// Каталог месторождений — CRUD над `/api/public/fields`. OSDU master-data
// Field, верхушка онтологии Field→Well→Wellbore. По паттерну CasingsPage
// (list + Editor в одном файле, refetch вместо оптимистики, list↔editor через
// search-параметры ?edit=N / ?new=1).
//
// Поля: name (обяз.), description (текст), country — код ISO 3166-1 alpha-2 (OSDU GeoContexts[]
// Country; сервер принимает только код из каталога, пусто = «не указана»), точка центра
// (OSDU Field.SpatialLocation, опциональна). Точка — ЦЕЛАЯ структура (Ш7 плана
// docs/osdu/PLAN_earth_point_standard.md): датум + широта + долгота. Голая пара без датума не точка,
// поэтому датум выбирается явно (WGS 84 не подставляется). Точку в сетке (метры) форма не вводит и не
// правит — показывает и оставляет как есть; её вводят в приложении. Бассейна нет с разбора OSDU
// 2026-09-28 (Q2): его дом — ссылка на запись Basin, которой у нас нет.

import { createSignal, createResource, createMemo, Show, For, onMount, onCleanup } from 'solid-js'
import type { Component } from 'solid-js'
import { Icon } from './Icons'
import {
  listFields,
  createField,
  updateField,
  deleteField,
  InUseError,
} from './api/fields'
import type { FieldDto, CreateFieldBody, UpdateFieldBody } from './api/fields'
import type { DatumId } from '@webtypes/DatumId'
import type { SpatialLocationDto } from '@webtypes/SpatialLocationDto'
import {
  DATUM_IDS,
  DATUM_NAMES,
  CRS_FAMILY_FULL_NAME_TEMPLATES,
} from '@webtypes/pointRegistry'

// ────────────────────── редактор ──────────────────────

type EditorState = {
  /// `null` — создаём. Иначе редактируем.
  source:        FieldDto | null
  name:          string
  description:   string
  /// Код ISO 3166-1 alpha-2 как ввели (регистр поднимается на вводе); пусто — не указана.
  country:       string
  /// Датум пары; `''` — не выбран. Умолчания нет: пара без объявленного датума не точка.
  datum:         DatumId | ''
  /// Координаты как строки — текстовое поле, парсим на submit. Пусто = не задано.
  latitude_deg:  string
  longitude_deg: string
  /// Точка задана в СЕТКЕ (метры): форма её не правит, здесь — подпись для показа. `null` — не так.
  grid_summary:  string | null
}

function emptyEditor(): EditorState {
  return {
    source:        null,
    name:          '',
    description:   '',
    country:       '',
    datum:         '',
    latitude_deg:  '',
    longitude_deg: '',
    grid_summary:  null,
  }
}

// Пара в объявленном датуме — единственная форма точки, которую правит эта страница.
type GeodeticLocation = Extract<SpatialLocationDto, { datum: DatumId }>
const isGeodetic = (l: SpatialLocationDto): l is GeodeticLocation => 'datum' in l

// Подпись сетки точки: «WGS84 / UTM zone 36N · N=… E=…».
const gridSummary = (l: Exclude<SpatialLocationDto, GeodeticLocation>): string => {
  const family = l.crs?.family
  const zone = l.crs?.zone
  const crs = family
    ? CRS_FAMILY_FULL_NAME_TEMPLATES[family].replace('{zone}', String(zone ?? '?'))
    : 'CRS не объявлена'
  return `${crs} · N=${l.northing_m} E=${l.easting_m}`
}

// Имя страны по коду ISO — подсказкой рядом с кодом. Каталог кодов судит сервер
// (`common_types::CountryCode`); здесь только читаемое имя, и незнакомый браузеру код его не получает.
const regionNames = new Intl.DisplayNames(['ru'], { type: 'region' })
const countryName = (code: string): string | null => {
  if (!/^[A-Z]{2}$/.test(code)) return null
  const name = regionNames.of(code)
  return name && name !== code ? name : null
}

function editorFrom(s: FieldDto): EditorState {
  return {
    source:        s,
    name:          s.name,
    description:   s.description,
    country:       s.country,
    datum:         s.location && isGeodetic(s.location) ? s.location.datum : '',
    latitude_deg:  s.location && isGeodetic(s.location) ? String(s.location.latitude_deg) : '',
    longitude_deg: s.location && isGeodetic(s.location) ? String(s.location.longitude_deg) : '',
    grid_summary:  s.location && !isGeodetic(s.location) ? gridSummary(s.location) : null,
  }
}

type EditorProps = {
  state:   EditorState
  onClose: () => void
  onSaved: () => void
}

const Editor: Component<EditorProps> = (props) => {
  const initial = props.state
  const [st, setSt] = createSignal<EditorState>(props.state)
  const [busy, setBusy] = createSignal(false)
  const [err, setErr] = createSignal<string | null>(null)

  const isCreate = () => st().source === null

  const isDirty = createMemo<boolean>(() => {
    const c = st()
    if (isCreate()) {
      return (
        c.name.trim().length > 0 ||
        c.description.trim().length > 0 ||
        c.country.trim().length > 0 ||
        c.datum !== '' ||
        c.latitude_deg.trim().length > 0 ||
        c.longitude_deg.trim().length > 0
      )
    }
    return (
      c.name !== initial.name
      || c.description !== initial.description
      || c.country !== initial.country
      || c.datum !== initial.datum
      || c.latitude_deg !== initial.latitude_deg
      || c.longitude_deg !== initial.longitude_deg
    )
  })

  const tryClose = () => {
    if (busy()) return
    if (isDirty() && !confirm('Несохранённые изменения будут потеряны. Закрыть?')) return
    props.onClose()
  }

  onMount(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        tryClose()
      }
    }
    window.addEventListener('keydown', onKey)
    onCleanup(() => window.removeEventListener('keydown', onKey))
  })

  // Опциональная координата: пусто → null; иначе число в диапазоне [lo, hi].
  // Возвращает `number | null` при успехе, либо строку-сообщение об ошибке.
  const parseCoord = (raw: string, label: string, lo: number, hi: number): number | null | string => {
    const trimmed = raw.trim()
    if (trimmed === '') return null
    const n = Number(trimmed)
    if (!Number.isFinite(n)) return `Поле ${label}: ожидается число`
    if (n < lo || n > hi) return `Поле ${label}: вне диапазона [${lo}, ${hi}]`
    return n
  }

  const onSubmit = async (e: Event) => {
    e.preventDefault()
    setErr(null)
    const cur = st()
    const nameTrimmed = cur.name.trim()
    if (nameTrimmed.length === 0) {
      setErr('Название обязательно')
      return
    }

    const lat = parseCoord(cur.latitude_deg,  'широта',  -90,  90)
    if (typeof lat === 'string') { setErr(lat); return }
    const lon = parseCoord(cur.longitude_deg, 'долгота', -180, 180)
    if (typeof lon === 'string') { setErr(lon); return }
    // Точка целиком или ничего: датум + обе координаты либо все три пусты.
    let point: SpatialLocationDto | null = null
    if (cur.grid_summary === null) {
      if (lat === null && lon === null) {
        if (cur.datum !== '') {
          setErr('Датум выбран, а координат нет: введите широту и долготу или снимите датум')
          return
        }
      } else {
        if (lat === null || lon === null) {
          setErr('Точка задаётся парой: нужны и широта, и долгота')
          return
        }
        if (cur.datum === '') {
          setErr('Выберите датум координат — без него пара не точка')
          return
        }
        point = { datum: cur.datum, latitude_deg: lat, longitude_deg: lon, crs: null }
      }
    }
    // Форму кода судим здесь (понятнее ответа 400), принадлежность каталогу — сервер.
    const country = cur.country.trim()
    if (country !== '' && !/^[A-Z]{2}$/.test(country)) {
      setErr('Страна: код ISO 3166-1 из двух латинских букв (UA, PL, US) или пусто')
      return
    }

    setBusy(true)
    try {
      if (isCreate()) {
        const body: CreateFieldBody = {
          name:        nameTrimmed,
          description: cur.description,
          country,
          location:    point,
        }
        await createField(body)
      } else {
        // PATCH: точка не тронута → ни `location`, ни `clear_location`. Точка в сетке форме недоступна
        // и всегда «не тронута».
        const pointTouched =
          cur.grid_summary === null && (
            cur.datum !== initial.datum
            || cur.latitude_deg !== initial.latitude_deg
            || cur.longitude_deg !== initial.longitude_deg
          )
        const body: UpdateFieldBody = {
          name:           nameTrimmed,
          description:    cur.description,
          country,
          location:       pointTouched ? point : null,
          clear_location: pointTouched && point === null,
        }
        await updateField(cur.source!.id, body)
      }
      props.onSaved()
    } catch (e) {
      setErr(String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form class="equipment-editor" onSubmit={onSubmit}>
      <div class="equipment-editor-head">
        <button class="btn btn-ghost" type="button" onClick={() => tryClose()} disabled={busy()}>
          ← К каталогу
        </button>
        <div class="equipment-editor-title">
          <div class="uppercase">{isCreate() ? 'Новое месторождение' : 'Редактирование'}</div>
          <h1>{isCreate() ? 'Создать месторождение' : st().name || 'Месторождение'}</h1>
        </div>
        <div class="equipment-editor-actions">
          <button class="btn" type="button" onClick={() => tryClose()} disabled={busy()}>
            Отмена
          </button>
          <button class="btn btn-primary" type="submit" disabled={busy() || !isDirty()}>
            {busy() ? 'Сохраняем…' : isCreate() ? 'Создать' : 'Сохранить'}
          </button>
        </div>
      </div>

      <Show when={err()}>
        <div class="card equipment-error">{err()}</div>
      </Show>

      <main class="equipment-form-panel">
        <section class="equipment-form-section">
          <div class="equipment-form-main">
            <label class="field">
              <div class="field-label">Название</div>
              <input
                class="field-input"
                value={st().name}
                onInput={(e) => setSt({ ...st(), name: e.currentTarget.value })}
                placeholder="Напр., Самотлорское"
                autofocus
              />
            </label>

            <label class="field">
              <div class="field-label">Описание</div>
              <textarea
                class="field-input"
                rows={3}
                value={st().description}
                onInput={(e) => setSt({ ...st(), description: e.currentTarget.value })}
                placeholder="Произвольное примечание"
              />
            </label>
          </div>

          <div class="field">
            <div class="field-label">Геология и расположение</div>
            <Show when={st().grid_summary}>
              {(g) => (
                <div class="muted text-sm" style={{ 'margin-bottom': 'var(--sp-2)' }}>
                  Точка задана в сетке: {g()}. Здесь она не правится — вводится в приложении.
                </div>
              )}
            </Show>
            <div class="dim-grid equipment-dim-grid">
              <div class="dim-cell">
                <div class="dim-label">
                  <span class="dim-symbol">Страна</span>
                  <span class="dim-hint">{countryName(st().country) ?? 'код ISO 3166-1, необязательно'}</span>
                </div>
                <input
                  class="field-input mono"
                  value={st().country}
                  onInput={(e) => setSt({ ...st(), country: e.currentTarget.value.toUpperCase() })}
                  maxLength={2}
                  placeholder="UA"
                />
              </div>

              <div class="dim-cell">
                <div class="dim-label">
                  <span class="dim-symbol">Датум</span>
                  <span class="dim-hint">система координат точки</span>
                </div>
                <select
                  class="field-input"
                  value={st().datum}
                  disabled={st().grid_summary !== null}
                  onChange={(e) => setSt({ ...st(), datum: e.currentTarget.value as DatumId | '' })}
                >
                  <option value="">— не выбран —</option>
                  <For each={DATUM_IDS}>
                    {(d) => <option value={d}>{DATUM_NAMES[d]}</option>}
                  </For>
                </select>
              </div>

              <div class="dim-cell">
                <div class="dim-label">
                  <span class="dim-symbol">Широта, °</span>
                  <span class="dim-hint">центр, [-90; 90]</span>
                </div>
                <input
                  class="field-input mono"
                  disabled={st().grid_summary !== null}
                  value={st().latitude_deg}
                  onInput={(e) => setSt({ ...st(), latitude_deg: e.currentTarget.value })}
                  inputmode="decimal"
                  placeholder="61.05"
                />
              </div>

              <div class="dim-cell">
                <div class="dim-label">
                  <span class="dim-symbol">Долгота, °</span>
                  <span class="dim-hint">центр, [-180; 180]</span>
                </div>
                <input
                  class="field-input mono"
                  disabled={st().grid_summary !== null}
                  value={st().longitude_deg}
                  onInput={(e) => setSt({ ...st(), longitude_deg: e.currentTarget.value })}
                  inputmode="decimal"
                  placeholder="76.8"
                />
              </div>
            </div>
          </div>
        </section>
      </main>
    </form>
  )
}

// ────────────────────── страница ──────────────────────

type FieldsPageProps = {
  fieldId?: number | null
  create?:  boolean
  onOpen:   (id: number) => void
  onCreate: () => void
  onBack:   () => void
}

export const FieldsPage: Component<FieldsPageProps> = (props) => {
  const [items, { refetch }] = createResource(() => listFields())
  const pageItems = createMemo<FieldDto[]>(() => items()?.items ?? [])
  const [delErr, setDelErr] = createSignal<string | null>(null)
  const [filterText, setFilterText] = createSignal('')

  const editor = createMemo<EditorState | null>(() => {
    if (props.create) return emptyEditor()
    if (props.fieldId == null) return null
    const item = pageItems().find((f) => f.id === props.fieldId)
    return item ? editorFrom(item) : null
  })

  const filtered = createMemo<FieldDto[]>(() => {
    const all = pageItems()
    const q = filterText().trim().toLowerCase()
    if (q.length === 0) return all
    return all.filter((f) =>
      f.name.toLowerCase().includes(q)
      || (f.description ?? '').toLowerCase().includes(q)
      || (f.country ?? '').toLowerCase().includes(q)
      || (countryName(f.country) ?? '').toLowerCase().includes(q)
    )
  })

  const openCreate = () => props.onCreate()
  const openEdit = (f: FieldDto) => props.onOpen(f.id)
  const closeEditor = () => props.onBack()
  const onSaved = () => {
    refetch()
    props.onBack()
  }

  const onDelete = async (f: FieldDto) => {
    if (!confirm(`Удалить «${f.name}»?`)) return
    setDelErr(null)
    try {
      await deleteField(f.id)
      refetch()
    } catch (err) {
      if (err instanceof InUseError) setDelErr(err.message)
      else setDelErr(String(err))
    }
  }

  // Страна: «UA — Украина», либо «—».
  const geoSummary = (f: FieldDto): string => {
    if (f.country === '') return '—'
    const name = countryName(f.country)
    return name ? `${f.country} — ${name}` : f.country
  }

  // В списке — WGS 84-производная точки (одна раскладка для любой формы ввода).
  const coordsSummary = (f: FieldDto): string =>
    f.location_wgs84 == null
      ? '—'
      : `${f.location_wgs84.latitude_deg.toFixed(6)}, ${f.location_wgs84.longitude_deg.toFixed(6)}`

  return (
    <Show
      when={editor()}
      keyed
      fallback={<div class="page page-wide">
      <div class="page-header">
        <div>
          <h1 class="page-title">Каталог месторождений</h1>
          <p class="page-subtitle">
            Месторождения (OSDU Field) — верхний уровень онтологии
            Field→Well→Wellbore. Страна и точка центра.
          </p>
        </div>
        <div class="row">
          <button class="btn btn-primary" onClick={openCreate}>
            <Icon name="plus" />Месторождение
          </button>
        </div>
      </div>

      <Show when={delErr()}>
        <div class="card" style={{ padding: 'var(--sp-3) var(--sp-4)', 'margin-bottom': 'var(--sp-4)', color: 'var(--status-err)' }}>
          {delErr()}
        </div>
      </Show>

      <Show when={items.loading}>
        <div class="card" style={{ padding: 'var(--sp-5)' }}>Загрузка…</div>
      </Show>

      <Show when={items.error}>
        <div class="card" style={{ padding: 'var(--sp-5)', color: 'var(--status-err)' }}>
          Ошибка: {String(items.error)}
        </div>
      </Show>

      <Show when={items() && items()!.total === 0}>
        <div class="card" style={{ padding: 'var(--sp-7)', 'text-align': 'center' }}>
          <div style={{ 'font-size': 'var(--fs-md)', 'margin-bottom': 'var(--sp-2)' }}>
            Каталог пуст
          </div>
          <div class="muted text-sm" style={{ 'margin-bottom': 'var(--sp-4)' }}>
            Создайте первое месторождение
          </div>
          <button class="btn btn-primary" onClick={openCreate}>
            <Icon name="plus" />Добавить месторождение
          </button>
        </div>
      </Show>

      <Show when={items() && items()!.total > 0}>
        <div class="row filter-row" style={{ gap: 'var(--sp-3)', 'margin-bottom': 'var(--sp-4)' }}>
          <div class="input" style={{ flex: 1, 'max-width': '320px' }}>
            <Icon name="search" />
            <input
              placeholder="Поиск по названию / стране"
              value={filterText()}
              onInput={(e) => setFilterText(e.currentTarget.value)}
            />
            <Show when={filterText().length > 0}>
              <button
                class="btn-icon btn-ghost"
                style={{ width: '24px', height: '24px' }}
                title="Очистить"
                onClick={() => setFilterText('')}
              >
                <Icon name="x" size={12} />
              </button>
            </Show>
          </div>
          <div class="muted text-sm filter-counter" style={{ 'margin-left': 'auto', 'align-self': 'center' }}>
            Показано {filtered().length} из {items()!.total}
          </div>
        </div>

        <div class="card">
          <table class="tbl">
            <thead>
              <tr>
                <th style={{ width: '110px' }}>ID</th>
                <th>Название</th>
                <th>Страна</th>
                <th>Координаты</th>
                <th>Описание</th>
                <th style={{ width: '80px' }} />
              </tr>
            </thead>
            <tbody>
              <For each={filtered()}>
                {(f) => (
                  <tr style={{ cursor: 'pointer' }} onClick={() => openEdit(f)}>
                    <td class="mono text-xs muted">#{f.id}</td>
                    <td style={{ 'font-weight': 500 }}>{f.name}</td>
                    <td class="text-xs">{geoSummary(f)}</td>
                    <td class="mono text-xs">{coordsSummary(f)}</td>
                    <td class="text-xs muted">{f.description || '—'}</td>
                    <td>
                      <button
                        class="btn-icon"
                        title="Удалить"
                        onClick={(ev) => {
                          ev.stopPropagation()
                          onDelete(f)
                        }}
                      >
                        <Icon name="x" />
                      </button>
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
          <Show when={filtered().length === 0}>
            <div style={{ padding: 'var(--sp-5)', 'text-align': 'center' }} class="muted text-sm">
              Ничего не найдено по фильтру
            </div>
          </Show>
        </div>
      </Show>

      </div>}
    >
      {(ed) => (
        <div class="page page-wide">
          <Editor state={ed} onClose={closeEditor} onSaved={onSaved} />
        </div>
      )}
    </Show>
  )
}
